import React, { useState } from "react";
import CommonAccountPanelView from "presentational/account/CommonAccountPanelView";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import { deleteUnlinkedCognitoUser } from "lib/accountDuplicationChecker";
import { sendMessage } from "lib/sentryHelper";
import TXT from "presentational/common/multinational/TXT";
import config from "lib/fr-config";
import Service from "lib/service-amp";
import { withStore } from "store/storeHandler";
import GaHelper from "lib/gaHelper";
import PixelHelper from "lib/pixelHelper";
import { getQueryObjFromUrl } from "lib/urlQueryParser";
import Restful from "lib/restful";
import PropTypes from "prop-types";

const REFERRAL_QUERY_INDEX = config.referralUrlQueryIndex;

AccountCreatePanel.propTypes = {
  cognitoUserInstance: PropTypes.object,
  email: PropTypes.string.isRequired,
  password: PropTypes.string,
  goNextUrl: PropTypes.func.isRequired
};

function AccountCreatePanel(props) {
  const {
    email,
    password,
    goNextUrl,
    viewerLocation,
    CognitoAuthActions,
    MyAccountActions,
    AppErrorActions,
    NoticeModalActions
  } = props;

  const [confirmationCode, setConfirmationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const getReferralInfoObj = () => {
    const nowUrlQryStr = window.location.search;
    const queryObj = getQueryObjFromUrl(nowUrlQryStr);
    const { refOutfit, refUsername } = REFERRAL_QUERY_INDEX;
    if (!!queryObj[refOutfit]) {
      return { [refOutfit]: queryObj[refOutfit] };
    } else if (!!queryObj[refUsername]) {
      return { [refUsername]: queryObj[refUsername] };
    } else {
      return {};
    }
  };

  const logOutCognito = () => {
    CognitoAuthActions.deleteAllData();
    MyAccountActions.setLogOut();
    Service.logOut();
  };

  const sendCognitoConfirmationCode = async () => {
    const { code, res } = await Service.confirmSignUp(email, confirmationCode);
    return { code, res };
  };

  const requestCognitoLogIn = async () => {
    const { code, res } = await Service.logIn(email, password);
    if (code === "error") {
      AppErrorActions.setSafeAppError(Error(res));
      return { isGood: false };
    } else if (code === "NotAuthorizedException") {
      return { isGood: false };
    }

    const tokenObj = res;
    CognitoAuthActions.setToken({ tokenObj });
    return { isGood: true, idToken: tokenObj.idToken.jwtToken };
  };

  const initNewUser = async idToken => {
    const refObj = getReferralInfoObj();
    const data = {
      location: viewerLocation,
      authType: "email",
      ...refObj
    };
    const { code, errInfo } = await Restful.ensureUserAccountAPI({
      data,
      idToken
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setFatalAppError(errObj);
      return false;
    } else if (
      code === "ReservedWord" ||
      code === "TooLongUsername" ||
      code === "InternalError"
    ) {
      const errObj = Error(`error : ${code}`);
      AppErrorActions.setFatalAppError(errObj);
      return false;
    }
    return true;
  };

  const clearUninitializeUser = async () => {
    sendMessage(`rollback warning => email:"${email}"`);

    try {
      const { message } = await deleteUnlinkedCognitoUser(email);
      if (message === "AvailableEmail") {
        return { complete: true };
      } else if (message === "AlreadyExist") {
        AppErrorActions.setSafeAppError(
          Error(`rollback error => email:"${email}"`)
        );
        return { complete: false };
      }
    } catch (error) {
      AppErrorActions.setFatalAppError(error);
      return { complete: false };
    }
  };

  const displayNotice = (type, input, callback) => {
    let content;
    switch (type) {
      case "accountCreationComplete":
        content = <TXT name="msg_signUpCompleted" />;
        break;
      case "codeNotFilledUp":
        content = (
          <TXT name="msg_enterVerificationCode" input={{ email: input }} />
        );
        break;
      case "wrongCode":
        content = <TXT name="msg_enterVorrectVeriCode" />;
        break;
      case "limitExceeded":
        content = <TXT name="msg_codeValidationReqExceeded" />;
        break;
      case "defaultSignUpError":
      default:
        content = <TXT name="msg_commonNetworkError" />;
        break;
    }
    NoticeModalActions.openModal({ content, callback });
  };

  const setAccountForCognito = async () => {
    if (isLoading) return null;

    await setIsLoading(true);

    const { code, res } = await sendCognitoConfirmationCode();
    if (code === "CodeMismatchException") {
      displayNotice("wrongCode");
      setIsLoading(false);
      return false;
    } else if (code === "LimitExceededException") {
      displayNotice("limitExceeded");
      setIsLoading(false);
      return false;
    } else if (code === "error") {
      AppErrorActions.setSafeAppError(Error(res));
      displayNotice("defaultSignUpError");
      setIsLoading(false);
      return false;
    }

    const { isGood, idToken } = await requestCognitoLogIn();
    if (!isGood) {
      displayNotice("defaultSignUpError");
      setIsLoading(false);
      return false;
    }

    const isGood2 = await initNewUser(idToken);
    if (!isGood2) {
      await clearUninitializeUser();
      displayNotice("defaultSignUpError");
      setIsLoading(false);
      return false;
    }

    logOutCognito();
    setIsLoading(false);
    return true;
  };

  const handleChange = (type, value) => {
    switch (type) {
      case "confirmationCode":
        setConfirmationCode(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    if (!confirmationCode) {
      displayNotice("codeNotFilledUp", email);
      return null;
    }

    const isOk = await setAccountForCognito();

    if (isOk) {
      displayNotice("accountCreationComplete", null, goNextUrl);

      PixelHelper.setPixelEvent({ type: "signUp" });
      GaHelper.setGAEvent({ type: "ma_sign_up" });
    }
  };

  return (
    <div>
      <CommonAccountPanelView
        title={<TXT name="titlePanel_signUp" />}
        textElement={["confirmationCode"]}
        onInputValueChange={handleChange}
        firstBtnText={<TXT name="btnPanel_submitSignUp" />}
        onFirstBtnClick={handleSubmit}
        emailForHelperTxt={email}
      />
      <LoadingModalView open={isLoading} />
    </div>
  );
}

export default withStore({
  stateMap: {
    userDevice: ["viewerLocation"]
  },
  dispatchMap: ["myAccount", "cognitoAuth", "appError", "noticeModal"]
})(AccountCreatePanel);
